import React from 'react';

/**
 * Charts for the two Company Report screens.
 *
 * SHARED ON PURPOSE. There are two company report pages, told apart only by the
 * case of the URL -- one for the admin, one for the company itself -- and they
 * have to show the same picture of the same month. Keeping the visuals in one
 * place is the only thing that has ever stopped them drifting.
 *
 * ── Why these forms ────────────────────────────────────────────────────────
 * The bill's headline is a single number, so it is a HERO NUMBER, not a chart.
 * The head count split across pricing bands, and the split of members across
 * goal outcomes, are both part-to-whole over a handful of categories, so they
 * are PROPORTIONAL STACKED BARS. How close members got to their goal is a
 * distribution over ordered bins, so it is a HISTOGRAM. Nothing here is a pie:
 * four segments at similar sizes cannot be compared by angle.
 *
 * ── Why these colours ──────────────────────────────────────────────────────
 * Both palettes were checked by computation, not by eye, for OKLab distance
 * under normal vision and under deuteranopia, protanopia and tritanopia, for
 * every ADJACENT pair in a stack (adjacency is what matters -- non-touching
 * segments have everything between them to separate them).
 *
 *   outcome palette   worst adjacent CVD dE 12.6, normal-vision dE 21.0
 *   tier palette      worst adjacent CVD dE 13.1, normal-vision dE 16.5
 *
 * The first attempt paired #00A878 with #0694F3 and FAILED: at the same
 * lightness they separate by dE 1.1 under tritanopia -- indistinguishable. The
 * green ramp below fixes that by separating the two "engaged" states on
 * lightness, which survives every kind of colour blindness.
 *
 * Identity is never carried by colour alone regardless: every segment has a
 * legend swatch AND its own count in text beside it.
 */

/* Ordered worst-to-best is deliberate: the bar reads as a funnel from engaged
   to disengaged, so the order carries meaning and must not be re-sorted by size. */
const OUTCOME = [
    { key: 'achievers', label: 'Goal achieved', colour: '#047857' },
    { key: 'shortfall', label: 'Trained, short of goal', colour: '#34D399' },
    { key: 'noActivity', label: 'Connected, no activity', colour: '#F59E0B' },
    { key: 'notConnected', label: 'No device connected', colour: '#CBD5E1' },
];

/* Up to four pricing bands. Ordered by band, never by size. */
const TIER_COLOURS = ['#E53E14', '#F59E0B', '#0694F3', '#64748B'];

/* Ordered bins, not categories -- a member at 99% is nearly a member at 100%. */
const PROGRESS_BINS = [
    { label: '0%', test: (p) => p <= 0 },
    { label: '1–25%', test: (p) => p > 0 && p <= 25 },
    { label: '26–50%', test: (p) => p > 25 && p <= 50 },
    { label: '51–75%', test: (p) => p > 50 && p <= 75 },
    { label: '76–99%', test: (p) => p > 75 && p < 100 },
    { label: '100%+', test: (p) => p >= 100 },
];

const dollars = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/**
 * A proportional stacked bar.
 *
 * Segments carry a 2px surface-coloured gap between them so two adjacent fills
 * never touch, and the ends are rounded to 4px against a flat baseline. Zero
 * values are dropped rather than drawn as slivers.
 */
const StackedBar = ({ segments, height = 14 }) => {
    const total = segments.reduce((s, x) => s + Math.max(0, Number(x.value) || 0), 0);
    const present = segments.filter((s) => Number(s.value) > 0);

    if (total <= 0) {
        return (
            <div
                className="w-full rounded bg-gray-100 dark:bg-gray-700"
                style={{ height }}
                aria-hidden="true"
            />
        );
    }

    return (
        <div className="flex w-full overflow-hidden rounded" style={{ height, gap: '2px' }}>
            {present.map((s) => (
                <div
                    key={s.key}
                    // Flex-basis rather than width: with a 2px gap between segments
                    // the percentages no longer sum to the track, and widths would
                    // overflow by (n-1) x 2px.
                    style={{ flex: `${(Number(s.value) / total) * 100} 0 0`, backgroundColor: s.colour }}
                    title={`${s.label}: ${Number(s.value).toLocaleString()}`}
                />
            ))}
        </div>
    );
};

/** Swatch + label + value. The value is text, so identity is never colour-alone. */
const LegendRow = ({ colour, label, value, suffix }) => (
    <div className="flex items-baseline gap-2 min-w-0">
        <span
            className="inline-block shrink-0 rounded-sm"
            style={{ width: 8, height: 8, backgroundColor: colour, transform: 'translateY(-1px)' }}
            aria-hidden="true"
        />
        <span className="truncate text-xs text-gray-500 dark:text-gray-300">{label}</span>
        <span className="ml-auto whitespace-nowrap text-xs font-medium text-gray-900 dark:text-white">
            {value}
            {suffix ? <span className="font-normal text-gray-400"> {suffix}</span> : null}
        </span>
    </div>
);

/* ------------------------------------------------------------------------- *
 * Billing
 * ------------------------------------------------------------------------- */

/**
 * The bill: a hero number, then how the head count splits across pricing bands.
 *
 * The band split is only drawn when MORE THAN ONE band is in play. A single
 * full-width bar labelled "Tier 1: 100%" tells a reader nothing they did not
 * already know from the total, and most companies sit entirely in one band.
 */
export const BillingSummary = ({
    billing,
    lines = [],
    subtotal,
    total,
    discountPercent,
    monthLabel,
    notBilled = false,
    notBilledNote,
    joinedDuringMonth = 0,
    joinedNote,
}) => {
    if (!billing) return null;

    const bands = lines.map((l, i) => ({
        key: `tier-${l.tier}`,
        label: `Tier ${l.tier}`,
        value: Number(l.users) || 0,
        colour: TIER_COLOURS[i % TIER_COLOURS.length],
        line: l,
    }));

    const active = Number(billing.counts?.active || 0);
    const inactive = Number(billing.counts?.inactive || 0);
    const pending = Number(billing.counts?.pendingInvites || 0);
    const memberMix = [
        { key: 'active', label: 'Active', value: active, colour: '#047857' },
        { key: 'inactive', label: 'Inactive', value: inactive, colour: '#CBD5E1' },
    ];

    const bandLabel = (l) =>
        l.to === null || l.to === undefined
            ? `${Number(l.from).toLocaleString()}+`
            : `${Number(l.from).toLocaleString()}–${Number(l.to).toLocaleString()}`;

    return (
        <div className="text-left">
            {/* ---- hero ---------------------------------------------------- */}
            <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">
                        {monthLabel}
                    </div>
                    {/* A month inside the free onboarding period still HAS members
                        and activity -- it just is not charged. Showing the roster with
                        "Not billed" where the amount goes reports the month honestly;
                        showing a bare sentence and an empty card reports nothing. */}
                    <div className="text-3xl font-semibold leading-tight text-gray-900 dark:text-white">
                        {notBilled ? 'Not billed' : dollars(total)}
                    </div>
                    {notBilled ? (
                        <div className="text-xs" style={{ color: '#b45309' }}>{notBilledNote}</div>
                    ) : discountPercent > 0 ? (
                        <div className="text-xs text-gray-500 dark:text-gray-300">
                            {dollars(subtotal)} less {discountPercent}% discount
                        </div>
                    ) : null}
                </div>
                <div className="text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {Number(billing.billableUsers || 0).toLocaleString()} billable
                    </div>
                    {/* An AVERAGE, never a rate: multiplying it by a different head
                        count does not give that company's bill. */}
                    <div className="text-xs text-gray-400">
                        {notBilled
                            ? `${dollars(total)} if it were billed`
                            : `avg ${dollars(billing.blendedRate)} / user`}
                    </div>
                </div>
            </div>

            {/* ---- how the head count splits across bands ------------------- */}
            {bands.length > 1 && (
                <div className="mt-5">
                    <div className="mb-1.5 text-[11px] uppercase tracking-wide text-gray-400">
                        Members by pricing band
                    </div>
                    <StackedBar segments={bands} />
                    <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                        {bands.map((b) => (
                            <LegendRow
                                key={b.key}
                                colour={b.colour}
                                label={`${b.label} · ${bandLabel(b.line)} @ ${dollars(b.line.rate)}`}
                                value={dollars(b.line.charge)}
                                suffix={`(${b.value})`}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* ---- who is on the roster ------------------------------------- */}
            <div className="mt-5">
                <div className="mb-1.5 flex items-baseline justify-between">
                    <span className="text-[11px] uppercase tracking-wide text-gray-400">
                        Members
                    </span>
                    <span className="text-[11px] text-gray-400">
                        {billing.billableModeLabel || billing.billableMode}
                    </span>
                </div>
                <StackedBar segments={memberMix} height={10} />
                <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1">
                    <LegendRow colour="#047857" label="Active" value={active} />
                    <LegendRow colour="#CBD5E1" label="Inactive" value={inactive} />
                </div>
                {pending > 0 && (
                    <p className="mt-2 text-[11px] text-gray-400">
                        {pending} invitation{pending === 1 ? '' : 's'} still outstanding
                        {billing.billableMode === 'invited' ? ' and charged' : ' and not charged'}.
                    </p>
                )}

                {/* ⚠️ THE TWO CARDS COUNT DIFFERENT DAYS, AND SAYING SO IS THE FIX.
                    Billing bills the roster as the month OPENED -- the client's rule
                    is that joiners during a billable month land on the next invoice.
                    Summary & Goals reports everyone who was on the programme BY THE
                    END of the month, because they trained. Side by side with no
                    explanation that reads as a contradiction: "1 billable" beside
                    "5 / 5 have a device connected". */}
                {joinedDuringMonth > 0 && (
                    <p className="mt-2 text-[11px]" style={{ color: '#b45309' }}>
                        {joinedDuringMonth} more joined during the month and {joinedDuringMonth === 1 ? 'is' : 'are'}{' '}
                        not on this invoice{joinedNote ? ` — ${joinedNote}` : ''}. They appear in
                        Summary &amp; Goals, which reports everyone on the programme by month end.
                    </p>
                )}
            </div>

            {/* ---- the single-band case, stated rather than drawn ------------ */}
            {bands.length === 1 && (
                <p className="mt-4 text-xs text-gray-500 dark:text-gray-300">
                    All {Number(bands[0].value).toLocaleString()} members fall in tier{' '}
                    {bands[0].line.tier} ({bandLabel(bands[0].line)}) at {dollars(bands[0].line.rate)}{' '}
                    each.
                </p>
            )}

            {/* ---- what this invoice IS -------------------------------------
                Not padding: an invoice that cannot be traced back to the pricing
                that produced it is not auditable, and "which version were we on
                in October" is the first question anyone asks about a disputed
                bill. */}
            <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 border-0 border-t border-solid border-gray-100 pt-4 dark:border-gray-700">
                <div>
                    <dt className="text-[11px] uppercase tracking-wide text-gray-400">Invoice date</dt>
                    {/* ⚠️ RENDERED IN UTC. The server builds this as the last
                        INSTANT of the billed month (23:59:59.999Z), so any viewer
                        east of Greenwich sees it roll into the next day — October's
                        invoice dated "Nov 1" on a screen whose whole subject is
                        which month a charge belongs to. */}
                    <dd className="text-xs text-gray-900 dark:text-white">
                        {billing.invoiceDate
                            ? new Date(billing.invoiceDate).toLocaleDateString(undefined, {
                                  day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
                              })
                            : '—'}
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] uppercase tracking-wide text-gray-400">Pricing</dt>
                    <dd className="text-xs text-gray-900 dark:text-white">
                        {billing.planVersion ? `Version ${billing.planVersion}` : 'Built-in rates'}
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] uppercase tracking-wide text-gray-400">Status</dt>
                    <dd className="text-xs text-gray-900 dark:text-white">
                        {notBilled
                            ? 'Onboarding — not billed'
                            : billing.source === 'invoice'
                                ? 'Finalised'
                                : 'Not yet finalised'}
                    </dd>
                </div>
                {billing.billableMode === 'active' && (
                    <div>
                        <dt className="text-[11px] uppercase tracking-wide text-gray-400">
                            Activity window
                        </dt>
                        <dd className="text-xs text-gray-900 dark:text-white">
                            {billing.inactiveLookbackMonths} months
                        </dd>
                    </div>
                )}
            </dl>
        </div>
    );
};

/* ------------------------------------------------------------------------- *
 * Summary & Goals
 * ------------------------------------------------------------------------- */

/** Splits participants the same four ways the downloadable report does. */
export const splitOutcomes = (participants = []) => {
    const achievers = participants.filter((r) => r.achievedGoal);
    const shortfall = participants.filter((r) => !r.achievedGoal && r.hasQualifiedActivity);
    const noActivity = participants.filter((r) => !r.hasQualifiedActivity && r.isConnected);
    const notConnected = participants.filter((r) => !r.isConnected);
    return { achievers, shortfall, noActivity, notConnected };
};

/**
 * How the month went, and how close the people who trained got.
 *
 * TWO QUESTIONS, TWO FORMS. "How is the company doing" is a part-to-whole over
 * four outcomes, so it is one stacked bar. "Are they close" is a distribution,
 * so it is a histogram over ordered bins — which the previous two grey-and-blue
 * rectangles could not answer at all, since a company where everyone reached 95%
 * and one where everyone reached 5% drew exactly the same picture.
 */
export const GoalsSummary = ({ participants = [], monthLabel, inProgress }) => {
    const { achievers, shortfall, noActivity, notConnected } = splitOutcomes(participants);
    const counts = {
        achievers: achievers.length,
        shortfall: shortfall.length,
        noActivity: noActivity.length,
        notConnected: notConnected.length,
    };
    const total = participants.length;
    const connected = total - counts.notConnected;

    const segments = OUTCOME.map((o) => ({
        key: o.key,
        label: o.label,
        colour: o.colour,
        value: counts[o.key],
    }));

    // Members who actually produced qualifying minutes. Including the rest would
    // pile everyone into the 0% bin and bury the shape that matters.
    const trained = [...achievers, ...shortfall];
    const bins = PROGRESS_BINS.map((b) => ({
        label: b.label,
        value: trained.filter((r) => b.test(Number(r.goalProgress) || 0)).length,
    }));
    const binMax = Math.max(1, ...bins.map((b) => b.value));

    const achievedPct = connected > 0 ? Math.round((counts.achievers / connected) * 100) : 0;

    return (
        <div className="text-left">
            {/* ---- hero: the one number an employer repeats in a meeting ---- */}
            <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">
                        {monthLabel}
                        {inProgress ? ' · in progress' : ''}
                    </div>
                    <div className="text-3xl font-semibold leading-tight text-gray-900 dark:text-white">
                        {achievedPct}%
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-300">
                        of connected members reached their goal
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {connected} / {total}
                    </div>
                    <div className="text-xs text-gray-400">have a device connected</div>
                </div>
            </div>

            {/* ---- outcome split -------------------------------------------- */}
            <div className="mt-5">
                <div className="mb-1.5 text-[11px] uppercase tracking-wide text-gray-400">
                    Where everyone landed
                </div>
                <StackedBar segments={segments} />
                <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                    {segments.map((s) => (
                        <LegendRow key={s.key} colour={s.colour} label={s.label} value={s.value} />
                    ))}
                </div>
            </div>

            {/* ---- distribution ---------------------------------------------- */}
            <div className="mt-5">
                <div className="mb-1.5 flex items-baseline justify-between">
                    <span className="text-[11px] uppercase tracking-wide text-gray-400">
                        How close they got
                    </span>
                    <span className="text-[11px] text-gray-400">
                        {trained.length} member{trained.length === 1 ? '' : 's'} who trained
                    </span>
                </div>

                {trained.length === 0 ? (
                    <p className="py-3 text-xs text-gray-400">
                        {connected === 0
                            ? 'Nobody has connected a device yet, so there is no activity to report.'
                            : `No qualifying activity recorded for ${monthLabel} yet.`}
                    </p>
                ) : (
                    <div>
                        {/* The plot and the axis labels are separate rows so the
                            baseline rule sits UNDER THE BARS rather than under the
                            text. Nothing else is drawn: with six labelled bars,
                            gridlines would be more ink than data. */}
                        <div
                            className="flex items-end gap-1.5 border-0 border-b border-solid border-gray-200 dark:border-gray-600"
                            style={{ height: 78 }}
                        >
                            {bins.map((b) => (
                                <div key={b.label} className="flex min-w-0 flex-1 flex-col items-center">
                                    {/* Value labels only where there is something to
                                        label -- a "0" over every empty bin is noise. */}
                                    <span className="mb-1 h-4 text-[10px] font-medium text-gray-900 dark:text-white">
                                        {b.value > 0 ? b.value : ''}
                                    </span>
                                    <div
                                        className="w-full"
                                        style={{
                                            // A floor of 2px for empty bins keeps the
                                            // axis readable without drawing a bar that
                                            // claims a value it does not have -- hence
                                            // the opacity drop as well.
                                            height: `${Math.max(b.value > 0 ? 4 : 2, (b.value / binMax) * 54)}px`,
                                            backgroundColor: b.label === '100%+' ? '#047857' : '#34D399',
                                            opacity: b.value > 0 ? 1 : 0.25,
                                            borderTopLeftRadius: 4,
                                            borderTopRightRadius: 4,
                                        }}
                                        title={`${b.label} of goal: ${b.value} member${b.value === 1 ? '' : 's'}`}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-1.5">
                            {bins.map((b) => (
                                <span
                                    key={b.label}
                                    className="mt-1 min-w-0 flex-1 truncate text-center text-[10px] text-gray-400"
                                >
                                    {b.label}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default { BillingSummary, GoalsSummary, splitOutcomes };
